import { useEffect, useRef, useState } from 'react';

/**
 * Reveal-on-scroll for DishCards.
 *
 * Every card observes through ONE shared IntersectionObserver — N cards, one
 * observer instance, not N — and each card self-unobserves the first time it
 * enters view (the reveal is one-shot, so there is nothing left to watch).
 * The reveal itself is a one-shot CSS animation on the card, so there is zero
 * ongoing cost once a card has appeared.
 *
 * When IntersectionObserver is unavailable (jsdom, very old browsers) the hook
 * reveals immediately, so content never stays stuck hidden.
 */
const revealCallbacks = new WeakMap<Element, () => void>();
let sharedObserver: IntersectionObserver | null = null;

function getSharedObserver(): IntersectionObserver | null {
  if (typeof IntersectionObserver === 'undefined') return null;
  if (!sharedObserver) {
    sharedObserver = new IntersectionObserver(
      (entries, observer) => {
        for (const entry of entries) {
          if (!entry.isIntersecting) continue;
          const reveal = revealCallbacks.get(entry.target);
          revealCallbacks.delete(entry.target);
          observer.unobserve(entry.target);
          reveal?.();
        }
      },
      // Start the reveal a little before the card is fully on screen.
      { rootMargin: '0px 0px 80px 0px' },
    );
  }
  return sharedObserver;
}

export function useRevealOnScroll<T extends HTMLElement>() {
  const ref = useRef<T | null>(null);
  const [revealed, setRevealed] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;

    const observer = getSharedObserver();
    if (!observer) {
      setRevealed(true);
      return;
    }

    revealCallbacks.set(el, () => setRevealed(true));
    observer.observe(el);
    return () => {
      revealCallbacks.delete(el);
      observer.unobserve(el);
    };
  }, []);

  return { ref, revealed };
}
